import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ClaimValue from '../Ladder/ClaimValue';
import DefinitionCaptureCard from './DefinitionCaptureCard';
import {
  pickDocumentNumber, readDocumentWidening, requestDocumentPublication, retireDocumentPublication, shareDocument,
  unshareDocument, type DocumentPick, type DocumentWidening,
} from '../../apis';
import type { DocumentNumber, LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { stromaAnswerCard } from './askPresentation';

/** Where a number stands against the warehouse, in words (#763). */
export const STANDING: Record<string, { label: string; color: 'success' | 'warning' | 'info' | 'default' }> = {
  matches: { label: 'Matches the warehouse', color: 'success' },
  mismatch: { label: 'Deck ≠ warehouse', color: 'warning' },
  ambiguous: { label: 'Which metric?', color: 'info' },
  not_in_catalog: { label: 'Not in the catalog', color: 'default' },
  unchecked: { label: 'Not checked', color: 'default' },
};

function Picked({ turn, number, pick, onChoose }: { turn: LandingAgentTurn; number: DocumentNumber; pick: DocumentPick;
  onChoose: (candidate: string) => void }) {
  if (pick.kind === 'clarify') {
    return (
      <Box role="group" aria-label={pick.prompt} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{pick.prompt}</Typography>
        {pick.options.map((o) => (
          <Button key={o.candidate} variant="outlined" size="small" sx={{ justifyContent: 'space-between', textTransform: 'none' }}
            onClick={() => onChoose(o.candidate)}>
            <span>{o.metric}{o.dimension ? ` by ${o.dimension}` : ''}{o.perspective_name ? ` · ${o.perspective_name}` : ''}</span>
            <span>{o.rendered ?? 'no value'}{o.match === 'matches' ? ' · reproduces the deck' : ''}</span>
          </Button>
        ))}
      </Box>
    );
  }
  if (pick.kind === 'define') {
    if (!pick.request) return <Typography variant="body2">The catalog has no definition of “{number.label}”.</Typography>;
    const asked = { ...turn, id: `${turn.id}:document:${number.key}`, userText: pick.question, definitionRequest: pick.request as any };
    return <DefinitionCaptureCard turn={asked} />;
  }
  if (pick.kind === 'unchecked') return <Typography variant="body2" color="text.secondary">{pick.reason}</Typography>;
  return (
    <>
    {pick.note && <Typography variant="caption" color="text.secondary" component="p">{pick.note}</Typography>}
    <Typography variant="body2">
      The deck says <b>{pick.deck}</b>; {pick.metric}{pick.dimension ? ` by ${pick.dimension}` : ''}{pick.perspective_name ? ` · ${pick.perspective_name}` : ''} in the warehouse is{' '}
      <ClaimValue claim={pick.claim as any}><b>{pick.warehouse ?? 'unavailable'}</b></ClaimValue>
      {pick.chosen ? ' — the metric you chose.' : pick.kind === 'mismatch' ? ' — they do not match.' : '.'}
    </Typography>
    </>
  );
}

/** Who can see an upload, in words: the workspace once it is in Knowledge, else the people it is shared with. */
export function audienceLine(state: DocumentWidening): string {
  if (state.publication?.state === 'approved') return 'In Knowledge: everyone in the workspace can use it';
  return state.shared_with.length ? `Shared with ${state.shared_with.join(', ')}` : 'Only you can see it';
}

/**
 * Widening an upload (#763 part B), its uploader's own controls: Share with people (B1: workspace members by address,
 * at once, each one removable) and Add to Knowledge (B2: a request a workspace admin reviews — an admin's own is added
 * at once — and removable). The state is read when the card mounts; until it arrives nothing is said about who can
 * see the file.
 */
function Widening({ uploadId, sessionId }: { uploadId: string; sessionId: string }) {
  const [state, setState] = useState<DocumentWidening | null>(null);
  const [sharing, setSharing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    readDocumentWidening(uploadId).then((s) => { if (live) setState(s); }, () => undefined);
    return () => { live = false; };
  }, [uploadId]);
  if (!state) return null;
  const act = (call: () => Promise<DocumentWidening>, then?: () => void) => {
    setBusy(true);
    setError(null);
    call().then((next) => { setState(next); then?.(); },
      (e: any) => setError(e?.response?.data?.error || 'That could not be saved. Try again.'))
      .finally(() => setBusy(false));
  };
  const readers = draft.split(/[\s,;]+/).map((r) => r.trim()).filter(Boolean);
  const publication = state.publication?.state;
  return (
    <Box data-testid="document-widening" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
      <Typography variant="caption" color="text.secondary">{audienceLine(state)}</Typography>
      {state.shared_with.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {state.shared_with.map((r) => <Chip key={r} size="small" label={r} disabled={busy}
          onDelete={() => act(() => unshareDocument(uploadId, r))} />)}
      </Box>}
      {publication === 'pending' && <Typography variant="caption">Waiting for review in Knowledge › Needs review.</Typography>}
      {publication === 'rejected' && <Typography variant="caption">
        Not added to Knowledge{state.publication?.by ? ` by ${state.publication.by}` : ''}: {state.publication?.reason}</Typography>}
      {sharing && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField size="small" fullWidth value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="Colleagues' e-mail addresses" inputProps={{ 'aria-label': 'Share with' }} />
        <Button size="small" variant="contained" disabled={busy || !readers.length}
          onClick={() => act(() => shareDocument(uploadId, readers), () => { setDraft(''); setSharing(false); })}>Share</Button>
      </Box>}
      {error && <Alert severity="error" variant="outlined">{error}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {!sharing && <Button size="small" onClick={() => setSharing(true)}>Share with people</Button>}
        {publication === 'approved'
          ? <Button size="small" disabled={busy} onClick={() => act(() => retireDocumentPublication(uploadId))}>Remove from Knowledge</Button>
          : <Button size="small" disabled={busy || publication === 'pending'}
              onClick={() => act(() => requestDocumentPublication(uploadId, sessionId))}>
              {state.approves_on_submit ? 'Add to Knowledge' : 'Request to add to Knowledge'}</Button>}
      </Box>
    </Box>
  );
}

/**
 * An attached file, read (#763): its numbers, each with where it stands against the warehouse, and the question back —
 * which one to talk about. A number is never mapped for the asker: one that more than one metric could be asks which,
 * showing each metric's warehouse value; a term the catalog does not have opens definition capture.
 */
export default function DocumentCard({ turn }: { turn: LandingAgentTurn }) {
  const answer = turn.document;
  const [open, setOpen] = useState<string | null>(null);
  const [picks, setPicks] = useState<Record<string, DocumentPick>>({});
  const [error, setError] = useState<string | null>(null);
  if (!answer) return null;
  const choose = (key: string, candidate?: string) => {
    setOpen(key);
    setError(null);
    pickDocumentNumber(answer.session_id, key, candidate).then(
      (pick) => setPicks((p) => ({ ...p, [key]: pick })),
      () => setError('That number could not be opened. Try again.'));
  };
  const doc = answer.document;
  return (
    <Paper variant="outlined" data-testid="document-card"
      sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, ...stromaAnswerCard }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2">{answer.name}</Typography>
        {doc && <Typography variant="caption" color="text.secondary">
          {[`${doc.pages} ${doc.pages === 1 ? 'page' : 'pages'}`, `${answer.claims.length} numbers`,
            doc.window ? `${doc.window.start} → ${doc.window.end}` : null].filter(Boolean).join(' · ')}
        </Typography>}
      </Box>
      {answer.status === 'error' && <Alert severity="error" variant="outlined">{answer.error}</Alert>}
      {!!answer.below_bar && <Typography variant="caption" color="text.secondary">
        {answer.below_bar} {answer.below_bar === 1 ? 'number is' : 'numbers are'} below your evidence bar: uploaded numbers are unconfirmed.
      </Typography>}
      {answer.warnings.map((w) => <Typography key={w} variant="caption" color="text.secondary">{w}</Typography>)}
      {answer.status === 'ok' && answer.claims.length > 0 && <Typography variant="body2" sx={{ fontWeight: 600 }}>{answer.prompt}</Typography>}
      {answer.claims.map((n) => {
        const standing = STANDING[n.match || 'unchecked'];
        const pick = picks[n.key];
        return (
          <Box key={n.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Button variant={open === n.key ? 'contained' : 'text'} color="inherit" onClick={() => choose(n.key)}
              aria-expanded={open === n.key} sx={{ justifyContent: 'flex-start', textTransform: 'none', gap: 1, textAlign: 'left' }}>
              <Typography component="span" variant="body2" sx={{ flex: 1 }}>{n.quote}</Typography>
              {n.page && <Typography component="span" variant="caption" color="text.secondary">{n.page_word} {n.page}</Typography>}
              <Chip size="small" label={standing.label} color={standing.color} variant="outlined" />
            </Button>
            {open === n.key && pick && <Box sx={{ pl: 2 }}>
              <Picked turn={turn} number={n} pick={pick} onChoose={(candidate) => choose(n.key, candidate)} />
            </Box>}
          </Box>
        );
      })}
      {error && <Alert severity="error" variant="outlined">{error}</Alert>}
      {answer.status === 'ok' && doc && answer.claims.length > 0 && <Widening uploadId={answer.upload_id} sessionId={answer.session_id} />}
    </Paper>
  );
}
